import { computed, ref } from 'vue';
import { HTTPAuth, HTTPNoAuth } from '@/services/http-common';
import { createQueryString, getFormattedErrorMessage } from '@/utils';
import { useToast } from './useToast';
import { IRequestError } from '@/types/error';
import { QueryRecord } from '@/types';

/**
 * @deprecated Use `ApiAuth`
 */
export function useApiAuth<T>(endpoint: string) {
  const data = ref<T>();
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const success = ref<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headers = ref<any>(null);
  const { toggleToast } = useToast();

  const get = (query?: QueryRecord) => {
    loading.value = true;
    error.value = null;
    success.value = false;

    const queryString = query ? `?${createQueryString(query)}` : '';

    return HTTPAuth.get<T>(`${endpoint}${queryString}`)
      .then(res => {
        data.value = res.data;

        if (res.status >= 200 && res.status <= 299) {
          success.value = true;
        }
      })
      .catch((err: IRequestError) => {
        data.value = undefined;
        error.value = err.message;

        console.error(err);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const post = (payload?: QueryRecord) => {
    loading.value = true;
    error.value = null;
    success.value = false;
    headers.value = null;

    return HTTPAuth.post<T>(endpoint, payload)
      .then(res => {
        data.value = res.data;
        headers.value = res.headers;

        if (res.status >= 200 && res.status <= 299) {
          success.value = true;
        }
      })
      .catch((err: IRequestError) => {
        error.value = err.message;

        toggleToast(true, 'error', getFormattedErrorMessage(err));
        console.error(err);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const patch = (payload?: QueryRecord) => {
    loading.value = true;
    error.value = null;
    success.value = false;

    return HTTPAuth.patch<T>(endpoint, payload)
      .then(res => {
        data.value = res.data;

        if (res.status >= 200 && res.status <= 299) {
          success.value = true;
        }
      })
      .catch((err: IRequestError) => {
        error.value = err.message;

        toggleToast(true, 'error', getFormattedErrorMessage(err));
        console.error(err);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const put = (payload?: QueryRecord) => {
    loading.value = true;
    error.value = null;
    success.value = false;

    return HTTPAuth.put<T>(endpoint, payload)
      .then(res => {
        data.value = res.data;

        if (res.status >= 200 && res.status <= 299) {
          success.value = true;
        }
      })
      .catch((err: IRequestError) => {
        error.value = err.message;

        toggleToast(true, 'error', getFormattedErrorMessage(err));
        console.error(err);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  return {
    loading: computed(() => loading.value),
    data: computed(() => data.value),
    error: computed(() => error.value),
    success: computed(() => success.value),
    headers: computed(() => headers.value),
    get,
    post,
    patch,
    put,
  };
}

/**
 * @deprecated Use `ApiNoAuth`
 */
export function useApiNoAuth<T>(endpoint: string) {
  const data = ref<T>();
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const success = ref<boolean>(false);

  const get = (query?: QueryRecord) => {
    loading.value = true;
    error.value = null;
    success.value = false;

    const queryString = query ? `?${createQueryString(query)}` : '';

    return HTTPNoAuth.get<T>(`${endpoint}${queryString}`)
      .then(res => {
        data.value = res.data;

        if (res.status >= 200 && res.status <= 299) {
          success.value = true;
        }
      })
      .catch(err => {
        data.value = undefined;
        error.value = err;

        console.error(err);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  return {
    loading: computed(() => loading.value),
    data: computed(() => data.value),
    error: computed(() => error.value),
    success: computed(() => success.value),
    get,
  };
}
